import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet } from '../services/api';
import SearchableSelect from './SearchableSelect';

/**
 * Configurações → Auditoria: quem entrou, quem criou, alterou ou excluiu o
 * quê, quando e de onde. Filtra no servidor (período, pessoa, ação) e
 * carrega de 50 em 50 — o histórico inteiro não cabe numa tela.
 */

const POR_PAGINA = 50;

const ACOES = [
  { value: 'create,update,delete', label: 'Todas as alterações' },
  { value: 'create', label: 'Criou' },
  { value: 'update', label: 'Alterou' },
  { value: 'delete', label: 'Excluiu' },
  { value: 'login', label: 'Login' },
  { value: 'logout', label: 'Logout' },
  { value: 'login_failed', label: 'Login Falhou' },
  { value: 'data_export', label: 'Exportação' },
  { value: 'backup_created', label: 'Backup' },
  { value: 'user_created', label: 'Usuário Criado' },
  { value: 'user_updated', label: 'Usuário Atualizado' },
  { value: 'user_deleted', label: 'Usuário Deletado' },
];

const CORES = {
  login: 'bg-[#13983D]',
  logout: 'bg-[#0974BA]',
  login_failed: 'bg-[#E83A3C]',
  password_reset: 'bg-[#F9B047]',
  user_created: 'bg-[#2E4974]',
  user_updated: 'bg-[#2E4974]',
  user_deleted: 'bg-[#E83A3C]',
  backup_created: 'bg-[#0974BA]',
  data_export: 'bg-[#0974BA]',
  create: 'bg-[#13983D]',
  update: 'bg-[#F9B047]',
  delete: 'bg-[#E83A3C]',
  other: 'bg-gray-400',
};

/** O diário da turma; numa aula, já no dia e na disciplina dela. */
const linkDoDiario = ({ turma, aula }) => {
  const params = new URLSearchParams();
  if (aula?.data) {
    params.set('data', aula.data);
    if (aula.disciplina) params.set('disciplina', aula.disciplina);
  }
  const busca = params.toString();
  return `/turmas/${turma.id}/diario${busca ? `?${busca}` : ''}`;
};

const formatarData = (texto) => (texto
  ? new Date(texto).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '-');

function AuditoriaAcessos({ darkMode, cardClass, titleClass, mutedClass }) {
  const [acao, setAcao] = useState('');
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [logs, setLogs] = useState([]);
  const [temMais, setTemMais] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);

  // A busca vai ao servidor só quando a digitação para.
  useEffect(() => {
    const t = setTimeout(() => setBuscaAplicada(busca.trim()), 400);
    return () => clearTimeout(t);
  }, [busca]);

  const carregar = useCallback(async (offset) => {
    setCarregando(true);
    setErro(null);
    try {
      const pagina = await apiGet('/activity-logs/', {
        limit: POR_PAGINA,
        offset,
        action: acao || undefined,
        q: buscaAplicada || undefined,
        de: de || undefined,
        ate: ate || undefined,
      });
      const linhas = Array.isArray(pagina) ? pagina : [];
      // Quem salvou algo entre uma página e outra empurra as linhas: a
      // que já apareceu não se repete.
      setLogs((anteriores) => {
        if (!offset) return linhas;
        const vistos = new Set(anteriores.map((l) => l.id));
        return [...anteriores, ...linhas.filter((l) => !vistos.has(l.id))];
      });
      setTemMais(linhas.length === POR_PAGINA);
    } catch (err) {
      setErro(err.message || 'Não foi possível carregar a auditoria.');
    } finally {
      setCarregando(false);
    }
  }, [acao, buscaAplicada, de, ate]);

  useEffect(() => {
    carregar(0);
  }, [carregar]);

  const textClass = darkMode ? 'text-slate-300' : 'text-slate-600';
  const campoClass = `px-3 py-2 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${
    darkMode
      ? 'bg-slate-700/50 border-slate-600 text-slate-200 placeholder-slate-500'
      : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'
  }`;
  const botaoClass = `px-3 py-2 rounded-lg text-sm ${darkMode ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'}`;
  const filtrando = acao || busca || de || ate;

  return (
    <div className={cardClass}>
      <h2 className={titleClass}>Auditoria</h2>
      <p className={`text-sm mb-4 ${mutedClass}`}>
        Quem entrou no sistema e quem criou, alterou ou excluiu registros. Cada
        &quot;Salvar&quot; vira uma linha — a chamada da turma inteira conta como uma.
      </p>

      <div className="flex flex-wrap items-end gap-2 mb-4">
        <input
          type="search"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Pessoa, CPF, turma, aluno..."
          className={`${campoClass} flex-1 min-w-[200px]`}
        />
        <SearchableSelect
          value={acao}
          onChange={setAcao}
          options={ACOES}
          emptyOptionLabel="Todas as ações"
          darkMode={darkMode}
          className="min-w-[190px]"
        />
        <label className={`text-xs ${mutedClass}`}>
          De
          <input type="date" value={de} max={ate || undefined} onChange={(e) => setDe(e.target.value)} className={`${campoClass} block mt-0.5`} />
        </label>
        <label className={`text-xs ${mutedClass}`}>
          Até
          <input type="date" value={ate} min={de || undefined} onChange={(e) => setAte(e.target.value)} className={`${campoClass} block mt-0.5`} />
        </label>
        {filtrando && (
          <button
            type="button"
            className={botaoClass}
            onClick={() => { setAcao(''); setBusca(''); setDe(''); setAte(''); }}
          >
            Limpar
          </button>
        )}
        <button type="button" onClick={() => carregar(0)} className={botaoClass}>
          Atualizar
        </button>
      </div>

      {erro && (
        <p className={`mb-4 p-3 rounded-lg text-sm ${darkMode ? 'bg-[#E83A3C]/20 text-[#E83A3C]' : 'bg-[#E83A3C]/5 text-[#E83A3C]'}`}>
          {erro}
        </p>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className={`border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <th className={`text-left py-2 px-2 font-medium ${mutedClass}`}>Data/Hora</th>
              <th className={`text-left py-2 px-2 font-medium ${mutedClass}`}>Usuário</th>
              <th className={`text-left py-2 px-2 font-medium ${mutedClass}`}>Ação</th>
              <th className={`text-left py-2 px-2 font-medium ${mutedClass}`}>Registro</th>
              <th className={`text-left py-2 px-2 font-medium ${mutedClass}`}>IP</th>
            </tr>
          </thead>
          <tbody>
            {!carregando && logs.length === 0 && (
              <tr>
                <td colSpan={5} className={`py-8 text-center ${mutedClass}`}>
                  Nenhum registro encontrado.
                </td>
              </tr>
            )}
            {logs.map((log) => (
              <tr key={log.id} className={`border-b last:border-0 ${darkMode ? 'border-slate-700/50 hover:bg-slate-700/30' : 'border-slate-100 hover:bg-slate-50'}`}>
                <td className={`py-2.5 px-2 ${mutedClass} whitespace-nowrap`}>{formatarData(log.created_at)}</td>
                <td className={`py-2.5 px-2 ${textClass}`}>
                  <div className="font-medium">
                    {log.person_name || (log.user ? log.username : 'Anônimo')}
                  </div>
                  <div className={`flex items-center gap-2 mt-0.5 text-xs ${mutedClass}`}>
                    {log.user ? (
                      <>
                        {log.person_name && <span>{log.username}</span>}
                        {log.user_role && (
                          <span className={`px-1.5 py-0.5 rounded ${darkMode ? 'bg-slate-700 text-slate-400' : 'bg-slate-100 text-slate-500'}`}>
                            {log.user_role}
                          </span>
                        )}
                      </>
                    ) : log.person_name ? (
                      <span title="O CPF digitado é de um funcionário cadastrado que não tem conta de acesso">
                        Funcionário sem acesso ao sistema
                      </span>
                    ) : null}
                  </div>
                </td>
                <td className="py-2.5 px-2">
                  <span className="flex items-center space-x-2 whitespace-nowrap">
                    <span className={`w-2 h-2 rounded-full ${CORES[log.action] || 'bg-gray-400'}`}></span>
                    <span className={textClass}>{log.action_display}</span>
                  </span>
                </td>
                <td className={`py-2.5 px-2 ${textClass} min-w-[220px]`}>
                  <div>{log.description || '-'}</div>
                  {log.onde && (
                    <div className={`flex flex-wrap items-center gap-x-1.5 mt-0.5 text-xs ${mutedClass}`}>
                      {log.onde.escola && <span>{log.onde.escola.nome}</span>}
                      {log.onde.escola && log.onde.turma && <span>·</span>}
                      {log.onde.turma && (
                        <Link
                          to={linkDoDiario(log.onde)}
                          className="font-medium text-[#0974BA] hover:underline"
                          title={log.onde.aula?.data
                            ? `Abrir o diário da turma na aula de ${log.onde.aula.data.split('-').reverse().join('/')}`
                            : 'Abrir o diário da turma'}
                        >
                          Turma {log.onde.turma.nome} →
                        </Link>
                      )}
                    </div>
                  )}
                </td>
                <td className={`py-2.5 px-2 ${mutedClass} whitespace-nowrap font-mono text-xs`}>{log.ip_address || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex justify-center">
        {carregando ? (
          <span className={`text-sm ${mutedClass}`}>Carregando...</span>
        ) : temMais && (
          <button type="button" onClick={() => carregar(logs.length)} className={botaoClass}>
            Carregar mais
          </button>
        )}
      </div>
    </div>
  );
}

export default AuditoriaAcessos;
